import { CHOICE_FIELD_TYPES, type Ending, type FormDefinition, isField } from "@fobu/schema";
import { type ConditionContext, evaluateCondition } from "./conditions";
import { ExpressionError, type ExpressionScope, evaluateExpression, isEmptyValue, toNumber, toText } from "./expression";
import { type FormIndex, indexForm } from "./form-index";

export interface PageState {
  pageId: string;
  /** Sichtbare Blöcke (Felder und Inhalte) in Anzeigereihenfolge. */
  visibleBlockIds: string[];
  visibleFieldIds: string[];
}

export interface FlowIssue {
  code: "expression_error" | "unknown_variable" | "invalid_jump" | "loop_detected";
  message: string;
  pageId?: string;
  ruleId?: string;
}

export interface FlowState {
  /** Pfad durch das Formular, wie die ausfüllende Person ihn mit den aktuellen Antworten sieht. */
  pages: PageState[];
  /** Antworten nach Feld-ID – nur Felder, die auf dem Pfad liegen und sichtbar sind. */
  answers: Record<string, unknown>;
  variables: Record<string, number | string>;
  hidden: Record<string, string>;
  ending: Ending;
  issues: FlowIssue[];
}

export interface FlowInput {
  answers: Record<string, unknown>;
  /** Rohe URL-Parameter; nur deklarierte Hidden Fields werden übernommen. */
  hidden?: Record<string, string | undefined>;
}

/** Nur deklarierte Hidden Fields übernehmen, Defaults anwenden. */
export function resolveHidden(def: FormDefinition, raw: Record<string, string | undefined> = {}): Record<string, string> {
  const out: Record<string, string> = {};
  for (const h of def.hiddenFields) {
    const v = raw[h.name];
    if (typeof v === "string" && v !== "") out[h.name] = v.slice(0, 2000);
    else if (h.defaultValue !== undefined) out[h.name] = h.defaultValue;
  }
  return out;
}

export function initialVariables(def: FormDefinition): Record<string, number | string> {
  const vars: Record<string, number | string> = {};
  for (const v of def.variables) vars[v.name] = v.type === "number" ? toNumber(v.initial) : toText(v.initial);
  if (def.scoring.enabled && !(def.scoring.variable in vars)) vars[def.scoring.variable] = 0;
  return vars;
}

/** Ausdrucks-Scope: Antworten nach sprechendem Key statt nach ID. */
export function expressionScope(idx: FormIndex, answers: Record<string, unknown>, vars: Record<string, unknown>, hidden: Record<string, unknown>): ExpressionScope {
  const byKey: Record<string, unknown> = {};
  for (const f of idx.fields) byKey[f.key] = answers[f.id] ?? null;
  return { answers: byKey, vars, hidden };
}

function choicePoints(idx: FormIndex, fieldId: string, value: unknown): number {
  const field = idx.fieldById.get(fieldId);
  if (!field || !CHOICE_FIELD_TYPES.has(field.type) || field.type === "ranking" || !("choices" in field)) return 0;
  const ids = Array.isArray(value) ? value.map(String) : [toText(value)];
  return field.choices.reduce((sum, c) => (ids.includes(c.id) ? sum + (c.points ?? 0) : sum), 0);
}

/**
 * Berechnet den kompletten Formularverlauf für die gegebenen Antworten.
 * Deterministisch und ohne Seiteneffekte – identisch in Editor-Vorschau, Browser und Server.
 */
export function computeFlow(def: FormDefinition, input: FlowInput): FlowState {
  const idx = indexForm(def);
  const hidden = resolveHidden(def, input.hidden);
  const vars = initialVariables(def);
  const answers: Record<string, unknown> = {};
  const pages: PageState[] = [];
  const issues: FlowIssue[] = [];
  const visited = new Set<string>();
  const declaredVars = new Map(def.variables.map((v) => [v.name, v.type] as const));
  if (def.scoring.enabled && !declaredVars.has(def.scoring.variable)) declaredVars.set(def.scoring.variable, "number");

  let pageIndex: number | null = 0;
  let ending: Ending | null = null;

  while (pageIndex !== null) {
    const page = def.pages[pageIndex]!;
    if (visited.has(page.id)) {
      issues.push({ code: "loop_detected", message: `Seite „${page.title ?? page.id}“ wird erneut erreicht`, pageId: page.id });
      break;
    }
    visited.add(page.id);

    const ctx: ConditionContext = { answers, hidden, vars };
    const state: PageState = { pageId: page.id, visibleBlockIds: [], visibleFieldIds: [] };

    for (const block of page.blocks) {
      // Blöcke werden der Reihe nach ausgewertet: ein Feld kann von früheren Feldern derselben Seite abhängen.
      if (!evaluateCondition(block.visibleIf, ctx)) continue;
      state.visibleBlockIds.push(block.id);
      if (isField(block)) {
        state.visibleFieldIds.push(block.id);
        const value = input.answers[block.id];
        if (value !== undefined && !isEmptyValue(value)) answers[block.id] = value;
      }
    }
    pages.push(state);

    if (def.scoring.enabled) {
      const pts = state.visibleFieldIds.reduce((s, id) => s + choicePoints(idx, id, answers[id]), 0);
      vars[def.scoring.variable] = toNumber(vars[def.scoring.variable]) + pts;
    }

    let jump: (typeof page.rules)[number]["actions"][number] | null = null;
    for (const rule of page.rules) {
      if (!evaluateCondition(rule.when, { answers, hidden, vars })) continue;
      for (const action of rule.actions) {
        if (action.type === "set_variable") {
          const type = declaredVars.get(action.variable);
          if (!type) {
            issues.push({ code: "unknown_variable", message: `Variable „${action.variable}“ ist nicht deklariert`, pageId: page.id, ruleId: rule.id });
            continue;
          }
          try {
            const result = evaluateExpression(action.expression, expressionScope(idx, answers, vars, hidden));
            vars[action.variable] = type === "number" ? toNumber(result) : toText(result);
          } catch (e) {
            const msg = e instanceof ExpressionError ? e.message : String(e);
            issues.push({ code: "expression_error", message: msg, pageId: page.id, ruleId: rule.id });
          }
        } else if (!jump) {
          jump = action;
        }
      }
    }

    let nextIndex: number | null = pageIndex + 1 < def.pages.length ? pageIndex + 1 : null;
    if (jump?.type === "jump") {
      if (jump.to.kind === "ending") {
        const e = idx.endingById.get(jump.to.endingId);
        if (e) {
          ending = e;
          nextIndex = null;
        } else issues.push({ code: "invalid_jump", message: "Sprungziel-Endseite existiert nicht", pageId: page.id });
      } else {
        const target = idx.pageById.get(jump.to.pageId);
        if (target && target.index > pageIndex) nextIndex = target.index;
        else issues.push({ code: "invalid_jump", message: "Sprünge sind nur zu späteren Seiten erlaubt", pageId: page.id });
      }
    }
    pageIndex = nextIndex;
  }

  return { pages, answers, variables: vars, hidden, ending: ending ?? idx.defaultEnding, issues };
}

/** Navigation für den Renderer: nächste Seite nach `currentPageId` oder `null` = Endseite. */
export function nextPageId(flow: FlowState, currentPageId: string): string | null {
  const i = flow.pages.findIndex((p) => p.pageId === currentPageId);
  return i >= 0 && i + 1 < flow.pages.length ? flow.pages[i + 1]!.pageId : null;
}

export function previousPageId(flow: FlowState, currentPageId: string): string | null {
  const i = flow.pages.findIndex((p) => p.pageId === currentPageId);
  return i > 0 ? flow.pages[i - 1]!.pageId : null;
}

/** Fortschritt 0–1 auf Basis des aktuellen Pfads. */
export function progress(flow: FlowState, currentPageId: string): number {
  const i = flow.pages.findIndex((p) => p.pageId === currentPageId);
  return flow.pages.length === 0 || i < 0 ? 0 : i / flow.pages.length;
}
